// Розрахунок податкової накладної: цілі числа (BigInt), без float.
// Кількість і ціна — до 12 знаків (XSD: DGdecimal12_R), суми — у копійках, ПДВ рядка — у мільйонних гривні
// (DGdecimal6_R).
//
// Правила (docs/pn-registration-plan.md §5.4):
// - первинна величина рядка — сума без ПДВ (гр. 10); якщо задано кількість і ціну — сума = округлення
//   кількість × ціна до копійки; якщо задано суму (домовлена сума, Excel) — ціна похідна;
// - ціна з ПДВ: сума з ПДВ домовлена, база = округлення суми з ПДВ / (1 + ставка), ПДВ = різниця —
//   тож загальна сума з ПДВ не змінюється;
// - ПДВ рядка — до 6 знаків (Порядок №1307, гр. 11); ПДВ документа — округлення суми ПДВ рядків до копійки.
//   Останнє — гіпотеза з практики (не норма), позначена в плані; тримаємо її в одному місці — totals().

export const RATES = {
  20: { pct: 20n, label: "20%", xmlBase: "R01G7", xmlVat: "R03G7" },
  7: { pct: 7n, label: "7%", xmlBase: "R01G109", xmlVat: "R03G109" },
  14: { pct: 14n, label: "14%", xmlBase: "R01G14", xmlVat: "R03G14" },
  901: { pct: 0n, label: "0% (експорт)", xmlBase: "R01G9" },
  902: { pct: 0n, label: "0% (на митній території)", xmlBase: "R01G8" },
  903: { pct: 0n, label: "Звільнено від ПДВ", xmlBase: "R01G10" },
};

const M = 1_000_000n; // мільйонні — ПДВ рядка
const Q = 10n ** 12n;  // кількість і ціна: 12 знаків

// Половина вгору від нуля: a / b, b > 0
function divRound(a, b) {
  const neg = a < 0n;
  const x = neg ? -a : a;
  const q = (x * 2n + b) / (2n * b);
  return neg ? -q : q;
}

// «120,5» / «1 200.75» → ціле в одиницях 10⁻¹² (12 знаків). null — не число, більше maxScale знаків
// після коми або від'ємне
export function parseDec(s, maxScale = 12) {
  const t = String(s ?? "").replace(/[\s\u00a0]/g, "").replace(",", ".");
  const m = t.match(/^(\d+)(?:\.(\d*))?$/);
  if (!m) return null;
  const frac = m[2] || "";
  if (frac.replace(/0+$/, "").length > maxScale) return null;
  return BigInt(m[1]) * Q + BigInt((frac + "000000000000").slice(0, 12));
}

// 10⁻¹² → «120.5» (без зайвих нулів)
export function decText(v) {
  const neg = v < 0n, x = neg ? -v : v;
  const frac = String(x % Q).padStart(12, "0").replace(/0+$/, "");
  return `${neg ? "-" : ""}${x / Q}${frac ? "." + frac : ""}`;
}
// Копійки → «1234.50»
export function kopText(k) {
  const neg = k < 0n, x = neg ? -k : k;
  return `${neg ? "-" : ""}${x / 100n}.${String(x % 100n).padStart(2, "0")}`;
}
// Мільйонні гривні → «12.345600» (6 знаків, як у генераторі etax)
export function micro6(v) {
  const neg = v < 0n, x = neg ? -v : v;
  return `${neg ? "-" : ""}${x / M}.${String(x % M).padStart(6, "0")}`;
}
// Копійки → «1 234,50» для інтерфейсу
export function kopUi(k) {
  const [i, f] = kopText(k).split(".");
  return `${i.replace(/\B(?=(\d{3})+(?!\d))/g, " ")},${f}`;
}

// Рядок: { qty, price, sum, amountBy: "price" | "sum", rate }, priceMode документа: "net" | "gross".
// Повертає { ok, base (коп.), vat (мільйонні грн), gross (коп.), price і qty (10⁻¹²; ціна — без ПДВ) }
// або { ok: false, field } — яке поле не дає порахувати
export function calcRow(row, priceMode = "net") {
  const qty = parseDec(row.qty);
  if (qty == null || qty <= 0n) return { ok: false, field: "qty" };
  const r = RATES[row.rate];
  if (!r) return { ok: false, field: "rate" };
  const bySum = row.amountBy === "sum";
  let amount; // коп.: без ПДВ (net) або з ПДВ (gross)
  if (bySum) {
    const s = parseDec(row.sum, 2);
    if (s == null || s <= 0n) return { ok: false, field: "sum" };
    amount = s / 10_000_000_000n; // 10⁻¹² → копійки
  } else {
    const p = parseDec(row.price);
    if (p == null || p <= 0n) return { ok: false, field: "price" };
    amount = divRound(qty * p, 10n ** 22n); // 10⁻¹² × 10⁻¹² → копійки
  }
  let base, vat;
  if (priceMode === "gross") {
    base = divRound(amount * 100n, 100n + r.pct);
    vat = (amount - base) * 10_000n;
  } else {
    base = amount;
    vat = base * 10_000n * r.pct / 100n; // точно: база в копійках × ставка — до 4 знаків
  }
  // Ціна без ПДВ у XML: задана (net, за ціною) або похідна від бази
  const price = !bySum && priceMode !== "gross" ? parseDec(row.price) : divRound(base * 10n ** 10n * Q, qty);
  return { ok: true, qty, base, vat, gross: base + divRound(vat, 10_000n), price };
}

// Підсумки документа за ставками. vatKop — ПДВ ставки до копійки (округлення суми ПДВ рядків)
export function totals(rows, priceMode = "net") {
  const by = {};
  let vatMicro = 0n, base = 0n;
  for (const row of rows) {
    const c = calcRow(row, priceMode);
    if (!c.ok) continue;
    const t = (by[row.rate] ||= { base: 0n, vatMicro: 0n });
    t.base += c.base; t.vatMicro += c.vat;
    base += c.base; vatMicro += c.vat;
  }
  for (const t of Object.values(by)) t.vat = divRound(t.vatMicro, 10_000n);
  const vat = divRound(vatMicro, 10_000n);
  return { by, base, vat, total: base + vat };
}

// Ціна з націнкою: price × (1 + pct / 100), до копійки (половина — вгору). pct — відсотки, до 2 знаків після коми.
// Націнка 0 або ціни немає — ціна як була. Невірна ціна чи відсоток — null
export function withMarkup(price, pct) {
  const p = parseDec(price), m = parseDec(String(pct ?? "0"), 2);
  if (p == null || m == null) return null;
  if (m === 0n) return String(price);
  const k = (p * (100n * Q + m) / (100n * Q) + 5n * 10n ** 9n) / 10n ** 10n; // 10⁻¹² → копійки з округленням
  return `${k / 100n}.${String(k % 100n).padStart(2, "0")}`;
}
